import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Grid, Divider } from '@mui/material';
import { useTranslation } from 'next-i18next';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { Container } from './style';

import { APP_END_POINT } from 'src/api/app';
import courseAPI, { COURSE_END_POINT } from 'src/api/course';
import Heading from 'src/components/molecules/heading';
import CourseFilter from 'src/components/organisms/courseFilter';
import CourseResult from 'src/components/organisms/courseResult';
import { useQuery, useFilterItems } from 'src/helpers/use';
import { convertObjectToQueryString } from 'src/helpers/utils';
import { IBreadcrumb, ICategory } from 'src/interfaces';
import courseActions from 'src/redux/course/actions';
import { RootState } from 'src/redux/reducer';

const { add_course_api_query, clear_course_api_query } = courseActions;

const AllCourses = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { queryFilters }: any = useSelector((state: RootState) => ({
    queryFilters: state.Course.get('courseApiQueries'),
  }));
  const queryString = useQuery();
  const { data: categories } = useSWR<ICategory[]>(APP_END_POINT.CATEGORIES);
  const { filters } = useFilterItems(categories);
  const initCourseCount = 9; // 12 is original number of first course items to be displayed from jira card
  const loadMoreCount = 3; // 6 is original number of load more count items from jira card

  const breadcrumbList: IBreadcrumb[] = [
    { description: t('main_page'), url: '/' },
    { description: t('all_course.breadcrumbs.title') },
  ];
  const headerTitle = t('all_course.header.title');
  const headerDesc = t('all_course.header.desc');

  const courseApiResponse = useSWRInfinite((pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.data.items.length) return null;
    const limit = pageIndex === 0 ? initCourseCount : loadMoreCount;
    const offset = (() => {
      if (pageIndex !== 0) return pageIndex === 1 ? initCourseCount : limit * (pageIndex - 1) + initCourseCount;
      return 0;
    })();
    let url = `${COURSE_END_POINT.COURSES}?limit=${limit}&offset=${offset}`;
    const apiQueryString = convertObjectToQueryString(queryFilters);
    if (apiQueryString) {
      url += `&${apiQueryString}`;
    }
    return url;
  }, courseAPI.fetchCourses);

  useEffect(() => {
    if (queryString && Object.keys(queryString).length) {
      const convertQuery: any = Object.keys(queryString).reduce((acc: any, key: string) => {
        acc[key] = (queryString[key] as string)?.split(',');
        return acc;
      }, {});
      dispatch(add_course_api_query(convertQuery));
    } else if (queryString) {
      dispatch(clear_course_api_query());
    }
  }, [queryString]);

  useEffect(() => {
    if (queryString && Object.keys(queryString).length === 0 && queryString.constructor === Object) {
      dispatch(clear_course_api_query());
    }
    return () => {
      dispatch(clear_course_api_query());
    };
  }, []);

  return (
    <>
      <Heading
        contentHeight="180px"
        breadcrumbList={breadcrumbList}
        headerTitle={headerTitle}
        headerDesc={headerDesc}
      />
      <Container sx={{ mt: '1rem', mb: '2rem' }}>
        <Grid container spacing={2}>
          <Grid item lg={3} sx={{ display: { xs: 'none', lg: 'block' } }} className="side-bar">
            <CourseFilter filters={filters} />
          </Grid>
          <Grid item xs={12} lg={9} className="main-content">
            <CourseResult courseApiResponse={courseApiResponse} loadMoreCount={loadMoreCount} isShowSuggestion />
          </Grid>
        </Grid>
      </Container>
      <Divider />
    </>
  );
};

export default AllCourses;
